import React, { useState } from 'react';
import { Card, CardHeader, CardContent, Button } from '../../shared/design';
import { ChatIcon, CheckIcon } from './icons/UiIcons';
import { RESTAURATEUR_CONTACT_EMAIL } from '../../shared/restaurateurApplication';

const FAQ_ITEMS = [
    {
        q: "Comment configurer mon programme de fidélité ?",
        a: "Rendez-vous dans la section \"Programme Fidélité\" depuis la barre latérale. Vous pouvez choisir entre tampons, points ou cashback, définir les récompenses et activer des bonus de bienvenue."
    },
    {
        q: "Comment mes clients rejoignent-ils mon programme ?",
        a: "Vos clients scannent le QR code disponible dans \"Mon Établissement\" → \"Ma Carte\". Ils peuvent aussi rejoindre depuis l'application atable en cherchant votre établissement."
    },
    {
        q: "Comment créer une campagne marketing ?",
        a: "Dans \"Campagnes & Promos\", cliquez sur \"Nouvelle campagne\". Vous pouvez cibler des segments de clients (VIP, inactifs...) et choisir le canal (push, email, offre flash)."
    },
    {
        q: "Comment utiliser la caisse (POS) ?",
        a: "Cliquez sur \"Caisse / POS\" en bas de la barre latérale. Depuis l'écran d'accueil, créez une nouvelle commande, ajoutez des articles depuis votre menu, identifiez le client atable et procédez au paiement."
    },
    {
        q: "Les données du dashboard ne s'affichent pas, pourquoi ?",
        a: "Les KPIs se calculent à partir des transactions enregistrées via le POS et les visites validées. Si vous débutez, les données apparaîtront au fur et à mesure des premières utilisations."
    },
    {
        q: "Comment ajouter un membre de l'équipe ?",
        a: "Dans \"Équipe & Staff\", cliquez sur \"Inviter un staff\". Entrez l'email de votre collaborateur et choisissez son rôle (manager ou staff). Il recevra un email d'invitation."
    },
    {
        q: "Comment changer mon abonnement ?",
        a: "Rendez-vous dans \"Abonnement\" pour voir les plans disponibles. Vous pouvez upgrader ou gérer votre facturation via le portail Stripe sécurisé."
    },
];

const BookIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
    </svg>
);

const PlayIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" {...props}>
        <circle cx="12" cy="12" r="10" />
        <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" />
    </svg>
);

const FaqItem: React.FC<{ q: string; a: string }> = ({ q, a }) => {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-gray-100 dark:border-gray-800 last:border-0">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-4 px-6 text-left gap-4 hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
            >
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{q}</span>
                <svg className={`w-5 h-5 flex-shrink-0 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="px-6 pb-5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    {a}
                </div>
            )}
        </div>
    );
};

const HelpPage: React.FC = () => {
    const [message, setMessage] = useState('');
    const [sent, setSent] = useState(false);

    const handleSend = () => {
        if (!message.trim()) return;
        const subject = encodeURIComponent('Support atable — Demande d\'assistance');
        const body = encodeURIComponent(message);
        window.open(`mailto:${RESTAURATEUR_CONTACT_EMAIL}?subject=${subject}&body=${body}`);
        setSent(true);
        setMessage('');
        setTimeout(() => setSent(false), 3000);
    };

    const quickLinks = [
        { icon: <BookIcon className="w-5 h-5" />, title: 'Documentation', desc: 'Guides complets', href: '#' },
        { icon: <PlayIcon className="w-5 h-5" />, title: 'Tutoriels vidéo', desc: 'Prise en main rapide', href: '#' },
        { icon: <ChatIcon className="w-5 h-5" />, title: 'Chat en direct', desc: 'Lun–Ven 9h–18h', href: '#' },
    ];

    return (
        <div className="space-y-8 animate-fade-in max-w-3xl">
            <div>
                <h1 className="page-title">Aide & support</h1>
                <p className="page-subtitle">Trouvez des réponses ou contactez notre équipe</p>
            </div>

            {/* Liens rapides */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {quickLinks.map(item => (
                    <a
                        key={item.title}
                        href={item.href}
                        className="group flex items-center gap-4 p-5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl hover:border-brand-primary/60 hover:shadow-md transition-all"
                    >
                        <div className="w-11 h-11 rounded-xl bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                            {item.icon}
                        </div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 group-hover:text-brand-primary transition-colors">{item.title}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{item.desc}</p>
                        </div>
                    </a>
                ))}
            </div>

            {/* FAQ */}
            <Card padding="none">
                <div className="px-6 pt-6 pb-2">
                    <CardHeader title="Questions fréquentes" subtitle="Réponses aux questions les plus courantes" />
                </div>
                <CardContent className="px-0">
                    {FAQ_ITEMS.map((item, i) => <FaqItem key={i} q={item.q} a={item.a} />)}
                </CardContent>
            </Card>

            {/* Formulaire de contact */}
            <Card padding="lg">
                <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1">Contacter le support</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Notre équipe vous répond sous 24h ouvrées</p>
                <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Décrivez votre problème ou votre question…"
                    rows={4}
                    className="w-full p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-400 dark:border-gray-700 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary resize-none transition-colors"
                />
                <div className="flex items-center gap-4 mt-4">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleSend}
                        disabled={!message.trim()}
                    >
                        Envoyer
                    </Button>
                    {sent && (
                        <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium inline-flex items-center gap-1.5">
                            <CheckIcon className="w-4 h-4" /> Message envoyé !
                        </span>
                    )}
                </div>
            </Card>

            {/* Infos version */}
            <div className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-4">
                <span>atable v2.0 PRO</span>
                <span>·</span>
                <a href="#" className="hover:text-gray-600 dark:hover:text-gray-300">Conditions d'utilisation</a>
                <span>·</span>
                <a href="#" className="hover:text-gray-600 dark:hover:text-gray-300">Politique de confidentialité</a>
            </div>
        </div>
    );
};

export default HelpPage;
